//src/services/PatientPathologyService.js
import apiClient from "./apiClient";

const API_URL = `/patient_pathology`; // Use relative path since apiClient has baseURL

const getOrganizationIdQueryConfig = () => {
  const storedUser = localStorage.getItem("user");
  let organizationId = null;
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      organizationId = user?.organizationId || localStorage.getItem("organizationId");
      if (organizationId && typeof organizationId === "string") {
        organizationId = parseInt(organizationId, 10);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  return organizationId && organizationId > 0 ? { params: { organizationId } } : {};
};

// Function to create patient pathology entry
export const createPatientPathology = async (patientId, prescriptionId, labTestId, resultStatus = "PENDING", result = null, doctorId = null) => {
  try {
    const requestBody = {
      patientId,
      prescriptionId, // Can be null for direct bookings - backend will auto-create prescription
      labTestId,
      resultStatus,
      result
    };
    
    // Include doctorId if provided (optional - helps backend choose doctor for auto-created prescription)
    if (doctorId != null) {
      requestBody.doctorId = doctorId;
    }
    
    // apiClient adds organizationId as query param for /patient_pathology
    const response = await apiClient.post(`${API_URL}`, requestBody, getOrganizationIdQueryConfig());
    console.log("Patient pathology created:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating patient pathology:", error);
    throw error;
  }
};

// Function to update patient pathology
export const updatePatientPathology = async (patientId, prescriptionId, labTestId, resultStatus, result) => {
    try {
        const response = await apiClient.put(`${API_URL}/${patientId}`, {
            prescriptionId, // Pass prescriptionId
            labTestId, // Pass labTestId
            resultStatus, // Pass resultStatus
            result // Pass result (can be null)
        }, getOrganizationIdQueryConfig());
        console.log("Patient pathology updated:", response.data);
        return response.data; // Return the response data if needed
    } catch (error) {
        console.error("Error updating patient pathology:", error);
        throw error; // Rethrow the error for handling in the calling function
    }
};

// Function to update result and resultStatus
export const updatePatientPathologyResult = async (patientId, prescriptionId, labTestId, result, resultStatus, meta = {}) => {
    try {
        const response = await apiClient.put(`${API_URL}/result/${patientId}`, {
            prescriptionId, // Pass prescriptionId
            labTestId, // Pass labTestId
            result, // Pass the result
            resultStatus // Pass the resultStatus
        }, getOrganizationIdQueryConfig());
        console.log("Patient pathology result updated:", response.data);
        const status = String(resultStatus || "").toUpperCase();
        if (status === "COMPLETE" || status === "COMPLETED") {
          try {
            const { recordSampleResultEntered } = await import("./sampleTransactionService");
            await recordSampleResultEntered({
              patientId,
              prescriptionId,
              labTestId,
              result: true,
              sampleCollected: true,
              billed: meta.billed ?? true,
              reportId: meta.reportId ?? meta.report_id,
              invoiceId: meta.invoiceId ?? meta.invoice_id,
              patientType: meta.patientType || "opd",
              labTestName: meta.labTestName || meta.testName,
              patientName: meta.patientName,
            });
          } catch (sampleError) {
            console.warn("[PatientPathologyService] Sample transaction result not saved:", sampleError);
          }
        }
        return response.data; // Return the response data if needed
    } catch (error) {
        console.error("Error updating patient pathology result:", error);
        throw error; // Rethrow the error for handling in the calling function
    }
};

// Function to update patient pathology on reject
export const updatePatientPathologyReject = async (patientId, prescriptionId, labTestId) => {
    try {
        const response = await apiClient.put(`${API_URL}/reject/${patientId}`, {
            prescriptionId, // Pass prescriptionId
            labTestId, // Pass labTestId
            resultStatus: "REJECT", // Set resultStatus to REJECT
            result: null // Set result to null
        }, getOrganizationIdQueryConfig());
        console.log("Patient pathology updated on reject:", response.data);
        return response.data; // Return the response data if needed
    } catch (error) {
        console.error("Error updating patient pathology on reject:", error);
        throw error; // Rethrow the error for handling in the calling function
    }
};

export const updatePatientPathologyStatusBilled = async (patientId, prescriptionId, labTestId) => {
    try {
        const response = await apiClient.put(`${API_URL}/${patientId}`, {
            prescriptionId, // Pass prescriptionId
            labTestId, // Pass labTestId
            resultStatus: "BILLED", // Set resultStatus to BILLED
            result: null // Set result to null
        }, getOrganizationIdQueryConfig());
        return response.data;
    } catch (error) {
        console.error("Error updating patient pathology status to BILLED:", error);
        throw error;
    }
};

// Explicit helper to set resultStatus to COLLECTED
export const updatePatientPathologyCollect = async (patientId, prescriptionId, labTestId) => {
    try {
        const response = await apiClient.put(`${API_URL}/collected/${patientId}`, {
            prescriptionId,
            labTestId,
            resultStatus: "COLLECTED",
            result: null
        }, getOrganizationIdQueryConfig());
        return response.data;
    } catch (error) {
        console.error("Error updating patient pathology status to COLLECTED:", error);
        throw error;
    }
};

